// Cookie consent, asked only of visitors in the EEA/UK/Switzerland, where GA
// and Clarity need it (Clarity has enforced a consent signal there since
// 2025-10-31). Everyone else keeps today's behaviour with no banner.
//
// The region comes from the device's time zone: free, instant, no lookup
// service. It errs on the side of asking (e.g. Europe/Moscow also gets the
// banner), which is harmless.
//
// `?consent=show` in any URL forces the banner, to preview or change a choice.

const CONSENT_STORAGE_KEY = 'rivals-cookie-consent';
export const OPEN_CONSENT_EVENT = 'rivals:open-cookie-settings';

const CONSENT_TIME_ZONES = new Set([
  'Atlantic/Azores', 'Atlantic/Madeira', 'Atlantic/Canary', 'Atlantic/Reykjavik',
  'Atlantic/Faroe', 'Asia/Nicosia', 'Asia/Famagusta', 'Arctic/Longyearbyen',
]);

const getTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (error) {
    return '';
  }
};

const isForcedPreview = () => new URLSearchParams(window.location.search).get('consent') === 'show';

export const isConsentRegion = (timeZone = getTimeZone()) => (
  timeZone.startsWith('Europe/') || CONSENT_TIME_ZONES.has(timeZone) || isForcedPreview()
);

export const getStoredConsent = () => {
  try {
    const choice = JSON.parse(localStorage.getItem(CONSENT_STORAGE_KEY) || 'null')?.choice;
    return choice === 'granted' || choice === 'denied' ? choice : null;
  } catch (error) {
    return null;
  }
};

export const shouldShowConsentBanner = () => isForcedPreview() || (isConsentRegion() && !getStoredConsent());

// Same queue gtag.js reads; react-ga4 reuses it, so a consent default pushed
// here is processed before GA's config call.
const gtag = function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
};

const sendClarityConsent = (choice) => {
  if (typeof window.clarity !== 'function') return;
  window.clarity('consentv2', { ad_Storage: 'denied', analytics_Storage: choice });
};

// Before GA initializes: in the consent region, analytics storage starts
// denied (GA sends cookieless pings only) unless the visitor already agreed.
// Ads storage is always denied - the site runs no ads.
export const applyConsentDefaults = () => {
  if (!isConsentRegion()) return;
  const analytics = getStoredConsent() === 'granted' ? 'granted' : 'denied';
  gtag('consent', 'default', {
    analytics_storage: analytics,
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
};

// After Clarity loads: until it gets a signal it runs cookieless in the region.
export const syncClarityConsent = () => {
  const stored = getStoredConsent();
  if (isConsentRegion() && stored) sendClarityConsent(stored);
};

export const setConsent = (choice) => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
  } catch (error) {
    // Storage unavailable: the choice applies to this page only.
  }
  gtag('consent', 'update', { analytics_storage: choice });
  sendClarityConsent(choice);
};

export const openConsentSettings = () => window.dispatchEvent(new Event(OPEN_CONSENT_EVENT));
